<template>
  <div class="flex items-start w-full relative">
    <!-- Outside Toggle Button (Absolute on desktop to maintain centering) -->
    <div
      class="hidden lg:flex absolute -left-6 top-6 items-center justify-center z-10"
    >
      <button
        class="flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-300 bg-white/60 dark:bg-[#0d1a20]/60 backdrop-blur-xl rounded-full border border-gray-300 dark:border-gray-800 shadow-md hover:shadow-xl w-12 h-12"
        @click="toggleExpanded()"
      >
        <i
          :class="{ 'rotate-90': isExpanded, 'pl-1': !isExpanded }"
          class="fa-solid fa-caret-right transition-transform duration-200 text-2xl"
        ></i>
      </button>
    </div>

    <!-- Main Card Container -->
    <div class="w-full relative">
      <!-- Mobile Toggle Button -->
      <div class="lg:hidden flex mb-3">
        <button
          class="flex items-center text-gray-500 dark:text-gray-400 font-bold text-xs uppercase tracking-wider bg-white/60 dark:bg-[#0d1a20]/60 backdrop-blur-xl px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-800 shadow-sm"
          @click="toggleExpanded()"
        >
          <i
            :class="{ 'rotate-90': isExpanded }"
            class="fa-solid fa-caret-right mr-2 transition-transform duration-200"
          ></i>
          {{ isExpanded ? "Collapse" : "Expand" }} Question
        </button>
      </div>

      <!-- The Glass Card -->
      <div
        class="bg-white/60 dark:bg-[#0d1a20]/60 backdrop-blur-xl border border-gray-300 dark:border-gray-800 shadow-xl rounded-3xl p-6 transition-all duration-300"
      >
        <div class="flex w-full mb-4 items-start gap-4 justify-between lg:pl-2">
          <h2
            class="text-2xl font-headline font-bold cursor-pointer w-full text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors pt-1"
            @click="toggleExpanded()"
          >
            {{ question.question }}
          </h2>

          <div class="flex items-center gap-2 shrink-0">
            <research-compass-question-dropdown
              :question="question"
              :pdf-base-url="pdfBaseUrl"
              :is-public="isPublic"
              @delete-question="deleteQuestion()"
              @clear-question="clearQuestion()"
              @copy-question-to-new-chat="copyQuestionToNewChat()"
              @download-pdf="openPdfModal"
            />
          </div>
        </div>

        <template v-if="isExpanded">
          <div class="py-2">
            <div
              v-if="question.searchTerm"
              class="flex flex-col w-full mb-2 gap-3"
            >
              <!-- Search Phrase Container -->
              <div
                class="bg-gray-100/50 dark:bg-[#12242b]/50 border border-gray-200 dark:border-gray-700 rounded-2xl px-5 py-4 w-full flex flex-col gap-2 shadow-inner group transition-all duration-300"
              >
                <div class="flex justify-between items-center">
                  <span
                    class="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 flex items-center"
                  >
                    <i
                      class="fas fa-search mr-1.5 text-blue-600/70 dark:text-blue-400/70"
                    ></i>
                    Search phrase
                  </span>
                  <div class="flex items-center gap-1.5">
                    <feedback-modal
                      sentiment="POSITIVE"
                      item-type="SEARCH_PHRASE"
                      :item-id="question.hashedId"
                      :ancillary-information-label="question.searchTerm"
                      :research-compass-hashed-id="
                        $route.params.researchCompassId
                      "
                    />
                    <feedback-modal
                      sentiment="NEGATIVE"
                      item-type="SEARCH_PHRASE"
                      :item-id="question.hashedId"
                      :ancillary-information-label="question.searchTerm"
                      :research-compass-hashed-id="
                        $route.params.researchCompassId
                      "
                    />
                  </div>
                </div>
                <span
                  class="text-sm font-semibold text-gray-900 dark:text-white leading-tight"
                  >{{ question.searchTerm }}</span
                >
              </div>

              <!-- Negative constraints display -->
              <div
                v-if="
                  question.negativeConstraints &&
                  question.negativeConstraints.length > 0
                "
                class="flex flex-wrap gap-2 items-center px-1"
              >
                <span
                  class="text-xs text-gray-500 dark:text-gray-400 font-medium"
                  >Excluded:</span
                >
                <span
                  v-for="term in question.negativeConstraints"
                  :key="term"
                  class="bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400 text-xs px-2.5 py-1 rounded-md font-semibold border border-red-200 dark:border-red-800/50"
                >
                  <i class="fas fa-minus mr-1 opacity-70"></i>{{ term }}
                </span>
              </div>
            </div>
            <div
              v-else-if="!shortAnswer || isClearing"
              class="bg-amber-500/5 dark:bg-amber-400/5 border border-amber-500/20 dark:border-amber-400/20 rounded-xl px-4 py-3 mb-4 text-sm text-amber-800 dark:text-amber-400 font-bold flex items-center shadow-sm"
            >
              <i class="fas fa-circle-notch fa-spin mr-2"></i>
              Generating search terms...
            </div>
          </div>

          <!-- Tethered Navigation System -->
          <div v-show="question.searchTerm" class="flex flex-col mt-2 relative">
            <div ref="tabContainer" class="w-full relative">
              <div
                class="mb-4 sticky top-4 z-40 lg:ml-6 flex flex-col items-start gap-0"
              >
                <!-- Tier 1: Main Tabs -->
                <div
                  class="inline-flex p-1.5 bg-gray-200/80 dark:bg-[#050a0c]/80 rounded-2xl border border-gray-300 dark:border-gray-800 backdrop-blur-xl shadow-md transition-colors relative z-20"
                >
                  <!-- Tabs Toggle Button -->
                  <button
                    class="hidden lg:flex absolute -left-6 top-1/2 -translate-y-1/2 items-center justify-center text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-300 bg-white/60 dark:bg-[#0d1a20]/60 backdrop-blur-xl rounded-full border border-gray-300 dark:border-gray-800 shadow-md hover:shadow-xl w-12 h-12 z-10"
                    @click="togglePanelsExpanded()"
                  >
                    <i
                      :class="{
                        'rotate-90': panelsExpanded,
                        'pl-1': !panelsExpanded,
                      }"
                      class="fa-solid fa-caret-right transition-transform duration-200 text-2xl"
                    ></i>
                  </button>

                  <div class="w-6 shrink-0 hidden lg:block"></div>

                  <button
                    ref="tab-sources"
                    class="px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 flex items-center gap-2"
                    :class="{
                      'bg-white dark:bg-[#12242b] text-blue-800 dark:text-blue-300 shadow-lg border border-gray-200 dark:border-gray-700':
                        panelsExpanded && selectedTab == 'sources',
                      'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white':
                        !panelsExpanded || selectedTab != 'sources',
                      'opacity-50': !loadedSources,
                    }"
                    @click="selectTab('sources')"
                  >
                    <i class="fas fa-file-lines opacity-70"></i>
                    Papers
                    <span
                      v-if="loadingSources"
                      class="text-gray-400 dark:text-gray-500 ml-1"
                      ><i class="fas fa-circle-notch fa-spin"></i
                    ></span>
                  </button>
                  <button
                    ref="tab-authors"
                    class="px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 flex items-center gap-2"
                    :class="{
                      'bg-white dark:bg-[#12242b] text-emerald-800 dark:text-emerald-300 shadow-lg border border-gray-200 dark:border-gray-700':
                        panelsExpanded && selectedTab == 'authors',
                      'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white':
                        !panelsExpanded || selectedTab != 'authors',
                      'opacity-50': !loadedAuthors,
                    }"
                    @click="selectTab('authors')"
                  >
                    <i class="fas fa-user-graduate opacity-70"></i>
                    Experts
                    <span
                      v-if="loadingAuthors"
                      class="text-gray-400 dark:text-gray-500 ml-1"
                      ><i class="fas fa-circle-notch fa-spin"></i
                    ></span>
                  </button>
                  <template
                    v-if="
                      collaborationSpaceMetadata?.isResearchCompassPatentEnabled
                    "
                  >
                    <button
                      ref="tab-patents"
                      class="px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 flex items-center gap-2"
                      :class="{
                        'bg-white dark:bg-[#12242b] text-purple-800 dark:text-purple-300 shadow-lg border border-gray-200 dark:border-gray-700':
                          panelsExpanded && selectedTab == 'patents',
                        'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white':
                          !panelsExpanded || selectedTab != 'patents',
                        'opacity-50': !loadedPatents,
                      }"
                      @click="selectTab('patents')"
                    >
                      <i class="fas fa-lightbulb opacity-70"></i>
                      Patents
                      <span
                        v-if="loadingPatents"
                        class="text-gray-400 dark:text-gray-500 ml-1"
                        ><i class="fas fa-circle-notch fa-spin"></i
                      ></span>
                    </button>
                  </template>
                  <template
                    v-if="
                      collaborationSpaceMetadata?.isResearchCompassIndustryPartnersEnabled
                    "
                  >
                    <button
                      ref="tab-industryPartners"
                      class="px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 flex items-center gap-2"
                      :class="{
                        'bg-white dark:bg-[#12242b] text-amber-800 dark:text-amber-300 shadow-lg border border-gray-200 dark:border-gray-700':
                          panelsExpanded && selectedTab == 'industryPartners',
                        'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white':
                          !panelsExpanded || selectedTab != 'industryPartners',
                        'opacity-50': !loadedIndustryPartners,
                      }"
                      @click="selectTab('industryPartners')"
                    >
                      <i class="fas fa-building opacity-70"></i>
                      Industry Partners
                      <span
                        v-if="loadingIndustryPartners"
                        class="text-gray-400 dark:text-gray-500 ml-1"
                        ><i class="fas fa-circle-notch fa-spin"></i
                      ></span>
                    </button>
                  </template>
                </div>

                <!-- Tier 2: Sub-Navigation (Tethered) -->
                <div
                  v-if="panelsExpanded && currentSubTabs.length > 0"
                  ref="subTabContainer"
                  class="absolute top-full mt-[-8px] transition-all duration-300 ease-out z-10 flex flex-col items-start pointer-events-none"
                  :style="subTabContainerStyle"
                >
                  <!-- Connector Bridge -->
                  <div
                    ref="bridge"
                    class="w-4 h-2 bg-gray-200/50 dark:bg-[#050a0c]/40 border-x border-gray-300 dark:border-gray-800 relative z-0 transition-all duration-300 ease-out"
                    :style="bridgeStyle"
                  ></div>

                  <div
                    class="inline-flex p-1 bg-gray-100/90 dark:bg-[#050a0c]/70 rounded-xl border border-gray-300 dark:border-gray-800 backdrop-blur-md shadow-lg pointer-events-auto"
                  >
                    <button
                      v-for="sub in currentSubTabs"
                      :key="sub.value"
                      class="px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all duration-200 flex items-center gap-1.5 whitespace-nowrap"
                      :class="[
                        selectedSubTab === sub.value
                          ? `bg-white dark:bg-[#12242b] shadow-sm border border-gray-200 dark:border-gray-700 ${selectedSubTabClass}`
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200',
                        !sub.isGenerated ? 'opacity-60' : '',
                      ]"
                      @click="selectedSubTab = sub.value"
                    >
                      <i
                        v-if="!sub.isGenerated && !sub.isLoading"
                        class="fas fa-sparkles text-amber-500 text-[9px]"
                      ></i>
                      <i
                        v-else-if="sub.isLoading"
                        class="fas fa-circle-notch fa-spin text-gray-400 dark:text-gray-500 text-[9px] ml-0.5"
                      ></i>
                      <i
                        v-else
                        :class="sub.icon"
                        class="opacity-70 text-[9px]"
                      ></i>
                      {{ sub.label }}
                    </button>
                  </div>
                </div>
              </div>

              <div v-show="panelsExpanded" class="pt-16">
                <div v-show="selectedTab == 'sources'" class="w-full">
                  <chat-question-card-sources-tab
                    ref="sourcesTab"
                    :question="question"
                    :short-answer="shortAnswer"
                    :loaded-sources="loadedSources"
                    :loading-sources="loadingSources"
                    :selected-sub-tab="selectedSubTab"
                    :is-clearing="isClearing"
                    :is-initiating="isInitiating"
                    :models="models"
                    :max-doc-count-display-string="maxDocCountDisplayString"
                    :model-display-string="modelDisplayString"
                    :computed-question-model="computedQuestionModel"
                    @get-sources="getSources"
                    @reload-research-compass="reloadResearchCompass"
                    @start-loading="startLoading"
                    @finish-loading="finishLoading"
                    @cancel-loading="cancelLoading"
                    @clear-question="clearQuestion"
                    @scroll-to-citation="scrollToCitation"
                    @change-sub-tab="changeSubTab"
                    @update-question-depth="updateQuestionDepth"
                    @update-question-model="updateQuestionModel"
                    @update:generating-table="generatingSourcesTable = $event"
                    @stream-start="scrollToAnswer"
                  />
                </div>
                <div v-show="selectedTab == 'authors'" class="w-full">
                  <chat-question-card-authors-tab
                    ref="authorsTab"
                    :question="question"
                    :loaded-authors="loadedAuthors"
                    :loading-authors="loadingAuthors"
                    :selected-sub-tab="selectedSubTab"
                    :include-researchers="isCollaborationSpace"
                    :generating-collaboration-opportunities="
                      generatingCollaborationOpportunities
                    "
                    @get-author-evaluations="getAuthorEvaluations"
                    @reload-research-compass="reloadResearchCompass"
                    @start-loading="startLoading"
                    @finish-loading="finishLoading"
                    @cancel-loading="cancelLoading"
                    @change-sub-tab="changeSubTab"
                    @update:generating-narrative="
                      generatingAuthorsNarrative = $event
                    "
                    @update:generating-table="generatingAuthorsTable = $event"
                    @update:generating-opportunities="
                      generatingCollaborationOpportunities = $event
                    "
                    @draft-collaboration-request="
                      $emit('draft-collaboration-request', $event)
                    "
                  />
                </div>
                <template
                  v-if="
                    collaborationSpaceMetadata?.isResearchCompassPatentEnabled
                  "
                >
                  <div v-show="selectedTab == 'patents'" class="w-full">
                    <chat-question-card-patents-tab
                      ref="patentsTab"
                      :question="question"
                      :loaded-patents="loadedPatents"
                      :loading-patents="loadingPatents"
                      :selected-sub-tab="selectedSubTab"
                      @get-patents="getPatents"
                      @reload-research-compass="reloadResearchCompass"
                      @start-loading="startLoading"
                      @finish-loading="finishLoading"
                      @cancel-loading="cancelLoading"
                      @change-sub-tab="changeSubTab"
                      @update:generating-table="generatingPatentsTable = $event"
                    />
                  </div>
                </template>
                <template
                  v-if="
                    collaborationSpaceMetadata?.isResearchCompassIndustryPartnersEnabled
                  "
                >
                  <div
                    v-show="selectedTab == 'industryPartners'"
                    class="w-full"
                  >
                    <chat-question-card-industry-partners-tab
                      ref="industryPartnersTab"
                      :question="question"
                      :loaded-industry-partners="loadedIndustryPartners"
                      :loading-industry-partners="loadingIndustryPartners"
                      :selected-sub-tab="selectedSubTab"
                      @get-industry-partners="getIndustryPartners"
                      @reload-research-compass="reloadResearchCompass"
                      @finish-loading="finishLoading"
                      @cancel-loading="cancelLoading"
                      @change-sub-tab="changeSubTab"
                      @update:generating-narrative="
                        generatingIndustryPartnersNarrative = $event
                      "
                      @update:generating-table="
                        generatingIndustryPartnersTable = $event
                      "
                    />
                  </div>
                </template>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
    <regular-modal ref="pdfModal" title="Download Research Compass">
      <template #body>
        <div class="p-6">
          <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
            The PDF report will include the following sections based on what has
            been generated:
          </p>
          <ul class="space-y-3 mb-6">
            <li class="flex items-center gap-3">
              <i class="fas fa-check-circle text-emerald-500"></i>
              <span class="text-gray-800 dark:text-gray-200 font-medium"
                >Answer & Sources</span
              >
            </li>
            <li class="flex items-center gap-3">
              <i
                :class="
                  loadedAuthors && hasAuthorNarrative
                    ? 'fa-check-circle text-emerald-500'
                    : loadedAuthors
                      ? 'fa-dot-circle text-blue-500'
                      : 'fa-clock text-amber-500'
                "
                class="fas"
              ></i>
              <span
                :class="{ 'opacity-60': !loadedAuthors }"
                class="text-gray-800 dark:text-gray-200 font-medium"
              >
                Research Experts
                <span
                  v-if="!loadedAuthors"
                  class="text-xs ml-2 italic font-normal text-gray-500"
                  >(Not generated yet)</span
                >
                <span
                  v-else-if="!hasAuthorNarrative"
                  class="text-xs ml-2 italic font-normal text-gray-500"
                  >(Explanation not generated yet)</span
                >
              </span>
            </li>
            <li
              v-if="collaborationSpaceMetadata?.isResearchCompassPatentEnabled"
              class="flex items-center gap-3"
            >
              <i
                :class="
                  loadedPatents && question.patents?.summary
                    ? 'fa-check-circle text-emerald-500'
                    : loadedPatents
                      ? 'fa-dot-circle text-blue-500'
                      : 'fa-clock text-amber-500'
                "
                class="fas"
              ></i>
              <span
                :class="{ 'opacity-60': !loadedPatents }"
                class="text-gray-800 dark:text-gray-200 font-medium"
              >
                Patents
                <span
                  v-if="!loadedPatents"
                  class="text-xs ml-2 italic font-normal text-gray-500"
                  >(Not generated yet)</span
                >
                <span
                  v-else-if="!question.patents?.summary"
                  class="text-xs ml-2 italic font-normal text-gray-500"
                  >(Summary not generated yet)</span
                >
              </span>
            </li>
            <li v-else class="flex items-center gap-3">
              <i class="fas fa-lock text-gray-400"></i>
              <span
                class="opacity-60 text-gray-800 dark:text-gray-200 font-medium"
              >
                Patents
                <span class="text-xs ml-2 italic font-normal text-gray-500"
                  >(Not included in your current plan)</span
                >
              </span>
            </li>
            <li
              v-if="
                collaborationSpaceMetadata?.isResearchCompassIndustryPartnersEnabled
              "
              class="flex items-center gap-3"
            >
              <i
                :class="
                  loadedIndustryPartners && hasIndustryPartnerNarrative
                    ? 'fa-check-circle text-emerald-500'
                    : loadedIndustryPartners
                      ? 'fa-dot-circle text-blue-500'
                      : 'fa-clock text-amber-500'
                "
                class="fas"
              ></i>
              <span
                :class="{ 'opacity-60': !loadedIndustryPartners }"
                class="text-gray-800 dark:text-gray-200 font-medium"
              >
                Industry Partners
                <span
                  v-if="!loadedIndustryPartners"
                  class="text-xs ml-2 italic font-normal text-gray-500"
                  >(Not generated yet)</span
                >
                <span
                  v-else-if="!hasIndustryPartnerNarrative"
                  class="text-xs ml-2 italic font-normal text-gray-500"
                  >(Explanation not generated yet)</span
                >
              </span>
            </li>
            <li v-else class="flex items-center gap-3">
              <i class="fas fa-lock text-gray-400"></i>
              <span
                class="opacity-60 text-gray-800 dark:text-gray-200 font-medium"
              >
                Industry Partners
                <span class="text-xs ml-2 italic font-normal text-gray-500"
                  >(Not included in your current plan)</span
                >
              </span>
            </li>
          </ul>
        </div>
      </template>
      <template #footer>
        <div
          class="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-800"
        >
          <button
            class="px-4 py-2 text-sm font-bold text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            @click="closePdfModal"
          >
            Cancel
          </button>
          <a
            :href="pdfUrlToDownload"
            target="_blank"
            class="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-blue-500/20 transition-all flex items-center gap-2"
            @click="closePdfModal"
          >
            <i class="fas fa-file-pdf"></i>
            Download Available Content
          </a>
        </div>
      </template>
    </regular-modal>
  </div>
</template>

<script>
import { nextTick } from "vue";
import { vOnClickOutside } from "@vueuse/components";
import * as utils from "../../utils";
import ResearchCompassQuestionDropdown from "./ResearchCompassQuestionDropdown.vue";
import ChatQuestionCardAuthorsTab from "./ChatQuestionCardAuthorsTab.vue";
import ChatQuestionCardSourcesTab from "./ChatQuestionCardSourcesTab.vue";
import ChatQuestionCardPatentsTab from "./ChatQuestionCardPatentsTab.vue";
import ChatQuestionCardIndustryPartnersTab from "./ChatQuestionCardIndustryPartnersTab.vue";
import FeedbackModal from "./FeedbackModal.vue";
import RegularModal from "../Modals/Regular.vue";

const { resolveRcApiUrl } = utils;

export default {
  directives: {
    onClickOutside: vOnClickOutside,
  },
  components: {
    ResearchCompassQuestionDropdown,
    ChatQuestionCardAuthorsTab,
    ChatQuestionCardSourcesTab,
    ChatQuestionCardPatentsTab,
    ChatQuestionCardIndustryPartnersTab,
    FeedbackModal,
    RegularModal,
  },
  props: {
    question: Object,
    collaborationSpaceMetadata: Object,
    pdfBaseUrl: String,
    model: String,
  },
  emits: [
    "reload-research-compass",
    "reload-followup-questions",
    "draft-collaboration-request",
  ],

  data() {
    return {
      pdfUrlToDownload: null,
      isClearing: false,
      isInitiating: false,
      optimisticModel: null,
      models: [
        {
          shortLabel: "GPT-5 mini",
          label: "GPT-5 mini (OpenAI)",
          value: "azure_gpt_5_mini",
        },
        {
          shortLabel: "Claude 4.6 Sonnet",
          label: "Claude 4.6 Sonnet (Anthropic)",
          value: "claude_4_6_sonnet",
        },
        {
          shortLabel: "Claude 5 Fable",
          label: "Claude 5 Fable (Anthropic)",
          value: "claude_fable_5",
        },
        {
          shortLabel: "Gemini 3.5 Flash-Lite",
          label: "Gemini 3.5 Flash-Lite (Google)",
          value: "gemini_3_5_flash_lite",
        },
        {
          shortLabel: "Gemini 3.7 Flash",
          label: "Gemini 3.7 Flash (Google)",
          value: "gemini_3_7_flash",
        },
        {
          shortLabel: "Gemini 3.8 Flash",
          label: "Gemini 3.8 Flash (Google)",
          value: "gemini_3_8_flash",
        },
        {
          shortLabel: "Gemini 3.0 Flash",
          label: "Gemini 3.0 Flash (Google)",
          value: "gemini_3_0_flash",
        },
        {
          shortLabel: "Gemini 3.1 Pro Preview",
          label: "Gemini 3.1 Pro Preview (Google)",
          value: "gemini_3_1_pro_preview",
        },
        {
          shortLabel: "Gemini 3.5 Flash",
          label: "Gemini 3.5 Flash (Google)",
          value: "gemini_3_5_flash",
        },
        {
          shortLabel: "Llama 4 Maverick",
          label: "Llama 4 Maverick (Meta)",
          value: "llama_4_maverick",
        },
        {
          shortLabel: "Mistral Small 3.1",
          label: "Mistral Small 3.1 (Mistral)",
          value: "mistral_3_1_small",
        },
      ],
      isExpanded: true,
      panelsExpanded: true,
      selectedTab: "sources",
      researchCompassQuestionChannel: null,
      subTabOffset: 0,
      selectedSubTab: "summary", // Default for Papers
      patentSearchIndex: 0,
      loadedSources: this.question.sourcesStatus?.message === "Completed",
      loadingSources: this.question.sourcesStatus?.isLoading,
      loadedAuthors: this.question.authorsStatus?.message === "Completed",
      loadingAuthors: this.question.authorsStatus?.isLoading,
      loadedPatents: this.question.patentsStatus?.message === "Completed",
      loadingPatents: this.question.patentsStatus?.isLoading,
      loadedIndustryPartners:
        this.question.industryPartnersStatus?.message === "Completed",
      loadingIndustryPartners: this.question.industryPartnersStatus?.isLoading,
      generatingSourcesTable: false,
      generatingAuthorsNarrative: false,
      generatingAuthorsTable: false,
      generatingIndustryPartnersNarrative: false,
      generatingIndustryPartnersTable: false,
      generatingPatentsTable: false,
      generatingCollaborationOpportunities: false,
      bridgeOffset: 0,
      ignoreDatabaseStatus: false, // Prevents state flapping during DB reloads
      activeBeganAt: null, // 🔒 Centralized Transaction Epoch (Timestamp Lock)
    };
  },
  computed: {
    isPublic() {
      return Boolean(this.$route.params.viewKey);
    },
    isCollaborationSpace() {
      return Boolean(this.$route.params.collaborationSpaceId);
    },
    maxDocCountDisplayString() {
      const docCount = this.question.maxDocCount || 10;
      if (docCount === 10) {
        return "Quick";
      }
      if (docCount === 15) {
        return "Balanced";
      }
      if (docCount === 20) {
        return "Deep";
      }
      return "Quick";
    },
    computedQuestionModel() {
      if (this.optimisticModel) {
        return this.optimisticModel;
      }
      const shortAnswers = this.question.answers
        ?.filter(
          (answer) =>
            (answer.answerType?.value || answer.answerType) === "SHORT_ANSWER",
        )
        ?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      if (shortAnswers?.length > 0 && shortAnswers[0].attributes?.modelName) {
        return shortAnswers[0].attributes.modelName;
      }
      return this.model;
    },
    modelDisplayString() {
      const modelValue = this.computedQuestionModel;
      const foundModel = this.models.find((m) => m.value === modelValue);
      return foundModel ? foundModel.shortLabel : "";
    },
    shortAnswer() {
      const shortAnswers = (this.question.answers || [])
        .filter(
          (answer) =>
            (answer.answerType?.value || answer.answerType) === "SHORT_ANSWER",
        )
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      if (shortAnswers.length > 0) {
        return shortAnswers[0].content;
      }
      return null;
    },
    selectedSubTabClass() {
      switch (this.selectedTab) {
        case "sources":
          return "text-blue-700 dark:text-blue-400";
        case "authors":
          return "text-emerald-700 dark:text-emerald-400";
        case "patents":
          return "text-purple-700 dark:text-purple-400";
        case "industryPartners":
          return "text-amber-700 dark:text-amber-400";
        default:
          return "text-blue-700 dark:text-blue-400";
      }
    },
    currentSubTabs() {
      if (this.selectedTab === "sources") {
        if (!this.loadedSources) return [];
        return [
          {
            label: "Answer",
            value: "summary",
            icon: "fas fa-align-left",
            isGenerated: true,
          },
          {
            label: "List",
            value: "list",
            icon: "fas fa-list",
            isGenerated: true,
          },
          {
            label: "Tabular Detail",
            value: "table",
            icon: "fas fa-table-list",
            isGenerated: this.hasPaperTable,
            isLoading: this.generatingSourcesTable,
          },
        ];
      }
      if (this.selectedTab === "authors") {
        if (!this.loadedAuthors) return [];
        return [
          {
            label: "List",
            value: "list",
            icon: "fas fa-list",
            isGenerated: true,
          },
          {
            label: "Explanation",
            value: "summary",
            icon: "fas fa-align-left",
            isGenerated: this.hasAuthorNarrative,
            isLoading: this.generatingAuthorsNarrative,
          },
          {
            label: "Comparison",
            value: "table",
            icon: "fas fa-table-list",
            isGenerated: this.hasAuthorTable,
            isLoading: this.generatingAuthorsTable,
          },
          {
            label: "Collaboration Opportunities",
            value: "opportunities",
            icon: "fas fa-handshake",
            isGenerated: this.hasCollaborationOpportunities,
            isLoading: this.generatingCollaborationOpportunities,
          },
          {
            label: "What About [Researcher]?",
            value: "missing",
            icon: "fas fa-question-circle",
            isGenerated: true,
          },
        ];
      }
      if (this.selectedTab === "patents") {
        if (!this.loadedPatents) return [];
        return [
          {
            label: "List",
            value: "list",
            icon: "fas fa-list",
            isGenerated: true,
          },
          {
            label: "Explanation",
            value: "summary",
            icon: "fas fa-align-left",
            isGenerated: true, // Summary is generated with search
          },
          {
            label: "Comparison",
            value: "table",
            icon: "fas fa-table-list",
            isGenerated: this.hasPatentTable,
            isLoading: this.generatingPatentsTable,
          },
        ];
      }
      if (this.selectedTab === "industryPartners") {
        if (!this.loadedIndustryPartners) return [];
        return [
          {
            label: "List",
            value: "list",
            icon: "fas fa-list",
            isGenerated: true,
          },
          {
            label: "Explanation",
            value: "summary",
            icon: "fas fa-align-left",
            isGenerated: this.hasIndustryPartnerNarrative,
            isLoading: this.generatingIndustryPartnersNarrative,
          },
          {
            label: "Comparison",
            value: "table",
            icon: "fas fa-table-list",
            isGenerated: this.hasIndustryPartnerTable,
            isLoading: this.generatingIndustryPartnersTable,
          },
        ];
      }
      return [];
    },
    hasPaperTable() {
      return (this.question.answers || []).some(
        (a) => (a.answerType?.value || a.answerType) === "TABLE",
      );
    },
    hasCollaborationOpportunities() {
      return (this.question.answers || []).some(
        (a) =>
          (a.answerType?.value || a.answerType) ===
          "COLLABORATION_OPPORTUNITIES",
      );
    },
    hasAuthorNarrative() {
      return (this.question.answers || []).some(
        (a) => (a.answerType?.value || a.answerType) === "AUTHOR_NARRATIVE",
      );
    },
    hasAuthorTable() {
      return (this.question.answers || []).some(
        (a) => (a.answerType?.value || a.answerType) === "AUTHOR_TABLE",
      );
    },
    hasPatentTable() {
      return (this.question.answers || []).some(
        (a) => (a.answerType?.value || a.answerType) === "PATENT_TABLE",
      );
    },
    hasIndustryPartnerNarrative() {
      return (this.question.answers || []).some(
        (a) =>
          (a.answerType?.value || a.answerType) ===
          "INDUSTRY_PARTNER_NARRATIVE",
      );
    },
    hasIndustryPartnerTable() {
      return (this.question.answers || []).some(
        (a) =>
          (a.answerType?.value || a.answerType) === "INDUSTRY_PARTNER_TABLE",
      );
    },
    subTabContainerStyle() {
      return {
        left: `${this.subTabOffset}px`,
        transform: "translateX(-50%)",
      };
    },
    bridgeStyle() {
      return {
        left: `${this.bridgeOffset}px`,
      };
    },
    activeAxios() {
      if (this.isPublic) {
        return this.$api.axiosPublic;
      }
      return this.$api.axios;
    },
  },
  watch: {
    currentSubTabs() {
      this.updateSubTabPosition();
    },
    question() {
      this.isClearing = false;
      this.optimisticModel = null;
    },
    selectedTab() {
      this.updateSubTabPosition();
    },
    "question.searchTerm": function questionSearchTerm(newVal, oldVal) {
      if (newVal && !oldVal) {
        this.scrollToProgress();
      }
    },
    loadingSources(newVal, oldVal) {
      if (newVal && oldVal === false) {
        this.scrollToProgress();
      }
    },
    loadedSources(newVal, oldVal) {
      if (newVal && oldVal === false) {
        this.scrollToAnswer();
      }
    },
    "question.sourcesStatus": function (newValue) {
      if (!newValue) return;

      // 1. Guard against any stale, delayed, or out-of-order database states
      if (this.activeBeganAt && newValue.beganAt) {
        const activeTime = new Date(this.activeBeganAt).getTime();
        const incomingTime = new Date(newValue.beganAt).getTime();
        if (!isNaN(incomingTime) && incomingTime < activeTime) {
          console.warn("[Concurrency] Stale DB state discarded:", newValue.beganAt);
          return; // Ignore older, lagging database reloads
        }
      }

      if (newValue.beganAt) {
        this.activeBeganAt = newValue.beganAt;
      }

      if (this.ignoreDatabaseStatus) return; // BYPASS DB OVERRIDES DURING TRANSACTIONS

      // Prevent state flapping: if we've already marked loading as finished/loaded,
      // ignore any transient "isLoading" states from slow DB writes.
      if (this.loadedSources && newValue.isLoading) {
        return;
      }
      this.loadingSources = newValue.isLoading;
      this.loadedSources = newValue.message === "Completed";
      if (newValue.isLoading || newValue.message === "Completed") {
        this.isInitiating = false;
      }
    },
    "question.authorsStatus": function (newValue) {
      if (!newValue) return;
      this.loadingAuthors = newValue.isLoading;
      this.loadedAuthors = newValue.message === "Completed";
    },
    "question.patentsStatus": function (newValue) {
      if (!newValue) return;
      this.loadingPatents = newValue.isLoading;
      this.loadedPatents = newValue.message === "Completed";
    },
    "question.industryPartnersStatus": function (newValue) {
      if (!newValue) return;
      this.loadingIndustryPartners = newValue.isLoading;
      this.loadedIndustryPartners = newValue.message === "Completed";
    },
  },
  mounted() {
    this.setUpChannel(this.question.hashedId);
    this.updateSubTabPosition();
    window.addEventListener("resize", this.updateSubTabPosition);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateSubTabPosition);
  },
  methods: {
    openPdfModal(url) {
      this.pdfUrlToDownload = url;
      this.$refs.pdfModal.toggleModal();
    },
    closePdfModal() {
      this.$refs.pdfModal.toggleModal();
      this.pdfUrlToDownload = null;
    },
    toggleExpanded() {
      this.isExpanded = !this.isExpanded;
    },
    togglePanelsExpanded() {
      this.panelsExpanded = !this.panelsExpanded;
    },
    updateSubTabPosition() {
      nextTick(() => {
        const activeTabRef = this.$refs[`tab-${this.selectedTab}`];
        const { subTabContainer } = this.$refs;
        const firstTab = this.$refs["tab-sources"];

        // Find the last visible tab ref
        let lastTab = this.$refs["tab-industryPartners"];
        if (!lastTab && this.$refs["tab-patents"])
          lastTab = this.$refs["tab-patents"];
        if (!lastTab) lastTab = this.$refs["tab-authors"];

        if (activeTabRef && subTabContainer && firstTab && lastTab) {
          const tabEl = activeTabRef.$el || activeTabRef;
          const firstTabEl = firstTab.$el || firstTab;
          const lastTabEl = lastTab.$el || lastTab;

          const tabCenter = tabEl.offsetLeft + tabEl.offsetWidth / 2;
          const subTabHalfWidth = subTabContainer.offsetWidth / 2;

          const minBound = firstTabEl.offsetLeft + subTabHalfWidth;
          const maxBound =
            lastTabEl.offsetLeft + lastTabEl.offsetWidth - subTabHalfWidth;

          // Clamp the tabCenter so the bar (with translateX(-50%)) doesn't go outside
          let clampedCenter = tabCenter;
          if (clampedCenter < minBound) {
            clampedCenter = minBound;
          } else if (clampedCenter > maxBound) {
            clampedCenter = maxBound;
          }

          this.subTabOffset = clampedCenter;

          // The bridge always points exactly to the center of the active tab
          // Relative to the start of the subTabContainer
          // Since subTabContainer is shifted by translateX(-50%), we need to account for that
          this.bridgeOffset =
            tabCenter - (this.subTabOffset - subTabHalfWidth) - 8;
        }
      });
    },
    selectTab(newTab) {
      if (this.selectedTab === newTab && this.panelsExpanded) {
        this.panelsExpanded = false;
      } else {
        this.selectedTab = newTab;
        this.panelsExpanded = true;
        // Reset subtab to default based on the new main tab
        if (newTab === "sources") {
          this.selectedSubTab = "summary";
        } else {
          this.selectedSubTab = "list";
        }

        nextTick(() => {
          this.updateSubTabPosition();
          if (this.$refs.tabContainer) {
            this.$refs.tabContainer.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        });
      }
    },
    changeSubTab(newSubTab) {
      this.selectedSubTab = newSubTab;
    },
    finishLoading(loadingType) {
      if (loadingType === "sources") {
        this.loadingSources = false;
        this.loadedSources = true;
      }
      if (loadingType === "authors") {
        this.loadingAuthors = false;
        this.loadedAuthors = true;
      }
      if (loadingType === "patents") {
        this.loadingPatents = false;
        this.loadedPatents = true;
      }
      if (loadingType === "industryPartners") {
        this.loadingIndustryPartners = false;
        this.loadedIndustryPartners = true;
      }
    },
    cancelLoading(loadingType) {
      if (loadingType === "sources") {
        this.loadingSources = false;
        this.loadedSources = false;
        if (this.question.sourcesStatus)
          this.question.sourcesStatus.isLoading = false;
      }
      if (loadingType === "authors") {
        this.loadingAuthors = false;
        this.loadedAuthors = false;
        if (this.question.authorsStatus)
          this.question.authorsStatus.isLoading = false;
      }
      if (loadingType === "patents") {
        this.loadingPatents = false;
        this.loadedPatents = false;
        if (this.question.patentsStatus)
          this.question.patentsStatus.isLoading = false;
      }
      if (loadingType === "industryPartners") {
        this.loadingIndustryPartners = false;
        this.loadedIndustryPartners = false;
        if (this.question.industryPartnersStatus)
          this.question.industryPartnersStatus.isLoading = false;
      }
    },
    startLoading(loadingType) {
      if (loadingType === "sources") {
        this.loadingSources = true;
        this.loadedSources = false;
      }
      if (loadingType === "authors") {
        this.loadingAuthors = true;
        this.loadedAuthors = false;
      }
      if (loadingType === "patents") {
        this.loadingPatents = true;
        this.loadedPatents = false;
      }
      if (loadingType === "industryPartners") {
        this.loadingIndustryPartners = true;
        this.loadedIndustryPartners = false;
      }
    },
    scrollToCitation(citationId) {
      if (this.selectedTab !== "sources") {
        this.selectTab("sources");
      }
      nextTick(() => {
        this.$refs.sourcesTab.scrollToCitation(citationId);
      });
    },
    getSources() {
      this.ignoreDatabaseStatus = true; // LOCK ON: Ignore any incoming stale DB status updates
      this.isInitiating = true;
      this.loadingSources = true;
      this.loadedSources = false;
      this.$emit("reload-research-compass");
      const url = resolveRcApiUrl(
        this.question.hashedId,
        "hydrate",
        this.$route.params,
      );

      this.activeAxios
        .post(url, {
          answer_model: this.computedQuestionModel,
        })
        .then((response) => {
          this.isClearing = false;
          this.isInitiating = false;
          this.ignoreDatabaseStatus = false; // LOCK OFF: DB is guaranteed to be in "Loading" state
          const newBeganAt = response?.data?.sourcesStatus?.beganAt;
          if (newBeganAt) {
            this.activeBeganAt = newBeganAt; // 🔒 Sync our client epoch with the DB's actual transaction start time
          }
        })
        .catch(() => {
          this.isInitiating = false;
          this.ignoreDatabaseStatus = false; // LOCK OFF
        });
    },
    deleteQuestion() {
      const url = resolveRcApiUrl(
        this.question.hashedId,
        "",
        this.$route.params,
      );
      // Use axiosWithoutGlobalToast to avoid triggering the global loader
      this.$api.axiosWithoutGlobalToast.delete(url).then(() => {
        this.$emit("reload-research-compass");
      });
    },
    clearQuestion() {
      this.isClearing = true;
      this.isInitiating = true;
      this.loadedSources = false;
      this.loadedAuthors = false;
      this.loadedPatents = false;
      this.loadedIndustryPartners = false;
      this.activeBeganAt = new Date().toISOString(); // 🔒 Lock out any outdated lag responses
      this.ignoreDatabaseStatus = true;
      if (this.$refs.sourcesTab) {
        this.$refs.sourcesTab.resetProgressState();
      }
      const url = resolveRcApiUrl(
        this.question.hashedId,
        "clear",
        this.$route.params,
      );

      this.activeAxios.post(url).then(() => {
        this.$emit("reload-research-compass");
        this.getSources();
      });
    },
    copyQuestionToNewChat() {
      this.$emit("reload-research-compass");
    },
    updateQuestionDepth(depth) {
      const url = resolveRcApiUrl(
        this.question.hashedId,
        "",
        this.$route.params,
      );
      this.activeAxios
        .patch(url, {
          max_doc_count: depth,
        })
        .then(() => {
          this.clearQuestion();
        });
    },
    updateQuestionModel(modelValue) {
      const selectedModelObj = this.models.find((m) => m.value === modelValue);
      if (selectedModelObj && selectedModelObj.disabled) {
        if (this.$toast) {
          this.$toast.warning(selectedModelObj.disabledReason || "Access to this model is currently suspended.");
        }
        return;
      }
      this.optimisticModel = modelValue;
      this.clearQuestion();
    },
    closeModeDropdown() {
      this.modeDropdownOpen = false;
    },
    closeModelDropdown() {
      this.modeDropdownOpen = false;
    },
    setUpChannel(questionHashedId) {
      if (this.researchCompassQuestionChannel) {
        this.researchCompassQuestionChannel.unsubscribe();
      }

      this.researchCompassQuestionChannel = this.$ably.channels.get(
        `${
          import.meta.env.VITE_ENV_CONFIG_NAMESPACE || ""
        }researchcompassquestion${questionHashedId}`,
      );

      this.researchCompassQuestionChannel.subscribe((message) => {
        let data;
        try {
          data =
            typeof message.data === "string"
              ? JSON.parse(message.data)
              : message.data;
        } catch (e) {
          console.error("Failed to parse websocket message", e);
          return;
        }

        if (!data) return;

        if (data.reload) {
          this.$emit("reload-research-compass");
        }
      });
    },
    getAuthorEvaluations() {
      this.loadingAuthors = true;
      this.loadedAuthors = false;
      const url = resolveRcApiUrl(
        this.question.hashedId,
        "evaluate-experts",
        this.$route.params,
      );
      this.activeAxios.post(url);
    },
    getPatents() {
      this.loadingPatents = true;
      this.loadedPatents = false;
      const url = resolveRcApiUrl(
        this.question.hashedId,
        "patent-search",
        this.$route.params,
      );
      this.activeAxios.post(url);
    },
    getIndustryPartners() {
      this.loadingIndustryPartners = true;
      this.loadedIndustryPartners = false;
      const url = resolveRcApiUrl(
        this.question.hashedId,
        "industry-partners",
        this.$route.params,
      );
      this.activeAxios.post(url);
    },
    reloadResearchCompass() {
      this.$emit("reload-research-compass");
    },
    scrollToProgress() {
      nextTick(() => {
        if (this.$refs.tabContainer) {
          this.$refs.tabContainer.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      });
    },
    scrollToAnswer() {
      nextTick(() => {
        if (this.$refs.tabContainer) {
          this.$refs.tabContainer.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      });
    },
  },
};
</script>
